import { FC, Fragment, useEffect, useState } from 'react';
import { Tr } from '@utils';
import { RoleWithAssignees, UserInvitation, User } from '@services';
import { Grid, MenuItem, TextField } from '@mui/material';
import { EntityDeleteConfirmationDialog, IEntityDeleteConfirmationDialogProps, StripedTableCell } from '@components/common';

export interface IRoleDeleteDialogProps extends Omit<IEntityDeleteConfirmationDialogProps, 'onAccept'> {
	roles: RoleWithAssignees[];
	entity?: RoleWithAssignees;
	onAccept?: (entity?: RoleWithAssignees) => void;
}

export const RoleDeleteDialog: FC<IRoleDeleteDialogProps> = ({ roles, entity, onAccept, ...props }) => {
	const [updatedRole, setUpdatedRole] = useState<RoleWithAssignees>();
	const { open } = props;

	useEffect(() => {
		setUpdatedRole(open ? new RoleWithAssignees(entity) : undefined);
	}, [open]);

	const updateRoleId = (assignee: UserInvitation | User, roleId: number) => {
		assignee.roleId = roleId;
		setUpdatedRole(new RoleWithAssignees(updatedRole));
	};

	return (
		<EntityDeleteConfirmationDialog
			content={<Tr.Portal path="roles.delete-confirmation-message" />}
			disableChildrenTypography
			buttonProps={{
				'data-testid': 'role-delete-confirmation-button',
				color: 'secondary',
				children: <Tr.Portal path="roles.delete-confirmation-button" />,
				disabled: !!updatedRole?.assignees.filter(({ roleId }) => roleId === updatedRole.id).length
			}}
			maxWidth="md"
			{...props}
			onAccept={() => onAccept?.(updatedRole)}
		>
			{updatedRole ? (
				<Grid container>
					{updatedRole.assignees.map((assignee, i) => {
						return (
							<Fragment key={`assignee-${assignee.email}-${i}`}>
								<StripedTableCell title={<Tr.Portal path="roles.collaborator" />} greyBg={!!(i % 2)}>
									{[assignee.name, assignee.surname].join(' ') || '-'}
								</StripedTableCell>
								<StripedTableCell greyBg={!!(i % 2)}>
									<TextField
										select
										value={assignee.roleId}
										onChange={(e) => updateRoleId(assignee, +e.target.value)}
										required
									>
										{(roles || []).map(({ id, name }) => (
											<MenuItem key={`role-${name}-${id}`} value={id} disabled={id === updatedRole.id}>
												{name}
											</MenuItem>
										))}
									</TextField>
								</StripedTableCell>
							</Fragment>
						);
					})}
				</Grid>
			) : null}
		</EntityDeleteConfirmationDialog>
	);
};
